import React, { useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import CustomTooltip from '../../../Tooltip';
import config, { cap, statuses } from '../../../../config';
import { getPath } from 'utils/Router';
import { getAvailableDevices } from '../../../modules';
import DataComponentOffer from '../../../DataComponent/DataComponentOffer';
import { I18nContext } from 'utils/I18n/context';

export function GoalsPageList(props) {
    const {
        t: propT,
        goalEntries = [],
        loading = false,
        totalPages = config.initialPage,
        regions = {},
        page,
        onAction
    } = props;

    const contextT = useContext(I18nContext);
    const t = contextT.t || propT;

    const getAvailableRegions = useCallback(
        (goal) => {
            let regs = [];
            if (goal.goal?.traffic_restrictions?.regions) {
                regs = goal.goal.traffic_restrictions.regions.map((id) => regions[id]);
            }
            return regs.join(', ');
        },
        [regions]
    );

    const onDataTableAction = useCallback(
        (state, type) => {
            if (type !== 'firstRender') onAction({ page: state.page });
        },
        [onAction]
    );

    const rows = useMemo(() => {
        goalEntries.forEach((goalEntry) => {
            const offer = goalEntry.offer;
            const defaultLink = offer.default_link;
            if (defaultLink) {
            }
        });
        const staticNamesOffer = t?.offersPage?.offer || {};
        
        return Object.values(
            (goalEntries || [])
                .reduce((acc, elem) => {
                    const offerId = elem.offer.id;
                    if(!acc[offerId]) {
                        acc[offerId] = elem;
                    } else {
                        // multiple goals; aggregate
                        acc[offerId].goal.title = '(' + t.offersPage.goalsList.multiple + ')';
                        acc[offerId].goal.description = '';

                        if (
                            Array.isArray(acc[offerId].goal?.traffic_restrictions?.regions)
                            ||
                            Array.isArray(elem.goal?.traffic_restrictions?.regions)
                        ) {
                            acc[offerId].goal.traffic_restrictions.regions = [
                                ...(acc[offerId].goal?.traffic_restrictions?.regions ?? []),
                                ...(elem.goal?.traffic_restrictions?.regions ?? []),
                            ];
                        };
                        if (
                            Array.isArray(acc[offerId].goal?.traffic_restrictions?.devices)
                            ||
                            Array.isArray(elem.goal?.traffic_restrictions?.devices)
                        ) {
                            acc[offerId].goal.traffic_restrictions.devices = [
                                ...(acc[offerId].goal?.traffic_restrictions?.devices ?? []),
                                ...(elem.goal?.traffic_restrictions?.devices ?? []),
                            ];
                        };
                        if([acc[offerId].status, elem.status].includes('active')) {
                            // any goal active -> show offer as active
                            acc[offerId].status = 'active';
                        } else {
                            // all paused -> show offer as paused
                            acc[offerId].status = 'paused';
                        }
                    }
                    return acc;
                }, {})
            ).map((goalEntry, i) => {
                const image = goalEntry.offer.image || cap;
                const devices = getAvailableDevices(goalEntry.goal.traffic_restrictions.devices);
                const regionsStr = getAvailableRegions(goalEntry);

                const offerPath = getPath('offer', { id: goalEntry.offer.id }) + '?tab=promo';
                const offerPathInfo = getPath('offer', { id: goalEntry.offer.id }) + '?tab=goals';

                let goalEntryDescription = goalEntry.goal.description.slice(0, 56);
                if (goalEntryDescription.length < goalEntry.goal.description.length) goalEntryDescription += ' ...';

                let goalEntryOfferTitle = goalEntry.offer.title.slice(0, 48);
                if (goalEntryOfferTitle.length < goalEntry.offer.title.length) goalEntryOfferTitle += ' ...';

                let goalEntryGoalTitle = goalEntry.goal.title.slice(0, 38);
                if (goalEntryGoalTitle.length < goalEntry.goal.title.length) goalEntryGoalTitle += ' ...';

                return {
                    accessIcon: (
                        <div className="text-center">
                            <Link className="offers__table-img offer-image" to={offerPathInfo}>
                                <div className="offer-image__bar">
                                    {goalEntry.status === statuses.paused ? (
                                        <CustomTooltip id={`icon-paused-${i}`} tooltip={staticNamesOffer.paused}>
                                            <div className="paused-icon">
                                                <i className="fa fa-pause-circle" />
                                            </div>
                                        </CustomTooltip>
                                    ) : null}
                                </div>
                                <img src={image} alt="" />
                            </Link>
                        </div>
                    ),
                    title: (
                        <div className="title-wrapper">
                            <div className="offers__item-descr">
                                <Link className="offer-name bold" to={offerPathInfo}>
                                    {goalEntry.status === statuses.paused ? '[ PAUSED ] ' : ''}
                                    {goalEntryOfferTitle}
                                </Link>
                                <div className="offers__item-description">
                                    <div className="offers__item-description-container">{goalEntryDescription}</div>
                                </div>
                                <div className="offers__item-goal">
                                    <span>{staticNamesOffer.goal}:</span>
                                    <span className="goal-name">{goalEntryGoalTitle}</span>
                                </div>
                                <p className="offers__item-locales">
                                    {staticNamesOffer.geo}:{' '}
                                    {goalEntry.goal.offer.locales.map((locale, j) => (
                                        <span className={`fi fi-${locale}`} key={j}></span>
                                    ))}
                                </p>
                                <div className="offers__item-creative">
                                    {Object.keys(goalEntry.creative_types).map((creative, k) =>
                                        goalEntry.creative_types[creative] > 0 ? (
                                            <div className="offers__item-creative-item" key={k}>
                                                {`${staticNamesOffer[creative]} (${goalEntry.creative_types[creative]})`}
                                            </div>
                                        ) : null
                                    )}
                                </div>
                            </div>
                            <div className="offers__item-promo-wrap">
                                <a href={offerPathInfo} className="promo-button">
                                    Promo
                                </a>
                                <a href={offerPath}>
                                    <span className="info fa fa-info-circle fa-3x"></span>
                                </a>
                            </div>
                        </div>
                    )
                };
            })
    }, [goalEntries, t, getAvailableRegions]);

    return (
        <div className="offers__list">
            <DataComponentOffer
                className="offers__table"
                onAction={onDataTableAction}
                totalPages={totalPages}
                page={page}
                data={rows}
                loading={loading}
            />
        </div>
    );
}

GoalsPageList.propTypes = {
    loading: PropTypes.bool,
    totalPages: PropTypes.number,
    page: PropTypes.number,
    headerNames: PropTypes.array,
    regions: PropTypes.object,
    goalEntries: PropTypes.array,
    onAction: PropTypes.func.isRequired,
    t: PropTypes.object
};
